/**
 * Payload-Schemas der minimierten Rohdaten (`RawRecordInput.payload`, Plan Welle 2 A, VB-2 b): Der Konnektor erzeugt
 * genau diese Formen, die TP2-Projektion liest genau diese Formen. Alle Schemas sind `strictObject`: Ein Feld außerhalb
 * der Form (Betreff, Text, Vorschau, Bcc, Anhänge, Beträge, Notizen, Chatter) kann nicht durchgereicht werden, weil es
 * kein Feld dafür gibt ([TP2] 5.1 bis 5.3; `raw_record.payload` wird gespeichert).
 *
 * - Microsoft 365, Quelle `m365.mail_meta`: `M365MailMetaPayloadSchema`, `M365FolderRemovalPayloadSchema`,
 *   `M365RoundEndPayloadSchema` (Mark-and-Sweep je Ordner).
 * - Microsoft 365, Quelle `m365.mail_radar`: `M365RadarPayloadSchema` (nur Beitragslinks; **kein Namenshinweis**).
 * - Odoo, Quelle `odoo.json2`: `OdooRecordPayloadSchema` (Allowlist je Modell), `OdooRunEndPayloadSchema`.
 *
 * Zeitpunkte sind ISO-8601-Zeichenketten in UTC (`…Z`); Hashes sind kleingeschriebenes Hex (64 Zeichen, SHA-256).
 * Zu-eins-Verweise in Odoo (`parent_id`, `partner_id`, `stage_id`) stehen nur als Nummer, nie mit Anzeigename.
 */
import { z } from 'zod';
import { EmailAddressSchema } from './refs.js';

const hex64 = z.string().regex(/^[0-9a-f]{64}$/);
const instant = z.iso.datetime();
const count = z.int().min(0);
const positiveId = z.int().min(1);

/** Ordnername wie im Strom `m365:folder:<name>` (`sources.ts`). */
const folder = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/);
/** Unveränderliche Nachrichten-ID von Graph; kein Leerraum und kein `:` (Trenner des Quellschlüssels). */
const immutableId = z.string().regex(/^[^\s:]{1,512}$/);

/** Absender oder Empfänger: Adresse (klein) und Kennzeichen „eigene Adresse“; kein Anzeigename. */
export const MailAddrSchema = z.strictObject({ address: EmailAddressSchema, self: z.boolean() });
export type MailAddr = z.infer<typeof MailAddrSchema>;

/** Position im Strom eines Ordners: `(stream_epoch, round_no, page_no, index)` lexikografisch ([TP2] 5.1). */
export const MailSeqSchema = z.strictObject({
  epoch: count,
  round: count,
  page: count,
  index: count,
});
export type MailSeq = z.infer<typeof MailSeqSchema>;

const RECIPIENTS_MAX = 100;

/** Mail-Metadaten (Quelle `m365.mail_meta`); ohne Betreff, Text, Vorschau, Anhänge, Bcc. */
export const M365MailMetaPayloadSchema = z.strictObject({
  v: z.literal(1),
  immutableId,
  folder,
  removed: z.literal(false),
  isDraft: z.boolean(),
  from: MailAddrSchema,
  to: z.array(MailAddrSchema).max(RECIPIENTS_MAX),
  cc: z.array(MailAddrSchema).max(RECIPIENTS_MAX),
  sentAt: instant,
  receivedAt: instant,
  /** `sha256hex(conversationId)`. */
  conversationKey: hex64,
  /** `sha256hex(internetMessageId)`. */
  internetMessageKey: hex64,
  seq: MailSeqSchema,
});
export type M365MailMetaPayload = z.infer<typeof M365MailMetaPayloadSchema>;

/** Entfernung aus einem Ordner (`@removed`): nur der Ordnerzustand ändert sich, auch ohne Metadatensatz. */
export const M365FolderRemovalPayloadSchema = z.strictObject({
  v: z.literal(1),
  immutableId,
  folder,
  removed: z.literal(true),
  seq: MailSeqSchema,
});
export type M365FolderRemovalPayload = z.infer<typeof M365FolderRemovalPayloadSchema>;

/**
 * Rundenende: letzter Datensatz jeder vollständigen Runde, Grundlage des Mark-and-Sweep (nicht gesehene Ordnerzustände
 * werden `present = false`).
 */
export const M365RoundEndPayloadSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal('round_end'),
  folder,
  roundId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  epoch: count,
  round: count,
  endSeq: MailSeqSchema,
  full: z.boolean(),
});
export type M365RoundEndPayload = z.infer<typeof M365RoundEndPayloadSchema>;

/** Ereignisarten des Radars (Musterergebnis der Betreffzeile; der Betreff selbst wird nie gespeichert). */
export const M365_RADAR_EVENT_KINDS = ['posted', 'commented', 'shared', 'unknown'] as const;

/** Beitragslink einer LinkedIn-Benachrichtigung: https, Host `linkedin.com`, `www.` oder `de.`, kein Port, keine Zugangsdaten. */
const RADAR_POST_URL = /^https:\/\/(?:www\.|de\.)?linkedin\.com\/[^\s"'<>]*$/;

/**
 * Radar-Datensatz (Quelle `m365.mail_radar`): nur Link, Zeit, Musterergebnis und Hash ([TP2] 5.2). Es gibt **keinen**
 * Namenshinweis, weil `raw_record.payload` gespeichert wird (RW-19).
 */
export const M365RadarPayloadSchema = z.strictObject({
  v: z.literal(1),
  /** `sha256hex(immutableId)`. */
  mailMessageKey: hex64,
  receivedAt: instant,
  postUrls: z.array(z.string().max(500).regex(RADAR_POST_URL)).min(1).max(5),
  eventKind: z.enum(M365_RADAR_EVENT_KINDS),
  patternVersion: z.string().regex(/^[A-Za-z0-9._-]{1,40}$/),
});
export type M365RadarPayload = z.infer<typeof M365RadarPayloadSchema>;

/** Modelle der Odoo-Allowlist ([TP2] 5.3); alles andere verwirft der Konnektor. */
export const ODOO_MODELS = ['res.partner', 'crm.lead', 'sale.order'] as const;

const odooPartner = z.strictObject({
  model: z.literal('res.partner'),
  id: positiveId,
  name: z.string().max(500),
  is_company: z.boolean(),
  parent_id: positiveId.nullable(),
  email: z.string().max(254).nullable(),
  website: z.string().max(2048).nullable(),
  active: z.boolean(),
  write_date: instant,
});

const odooLead = z.strictObject({
  model: z.literal('crm.lead'),
  id: positiveId,
  partner_id: positiveId.nullable(),
  type: z.enum(['lead', 'opportunity']),
  stage_id: positiveId.nullable(),
  probability: z.number().min(0).max(100),
  active: z.boolean(),
  create_date: instant,
  date_last_stage_update: instant.nullable(),
  write_date: instant,
});

const odooOrder = z.strictObject({
  model: z.literal('sale.order'),
  id: positiveId,
  partner_id: positiveId.nullable(),
  state: z.enum(['draft', 'sent', 'sale', 'done', 'cancel']),
  date_order: instant.nullable(),
  write_date: instant,
});

/** Odoo-Datensatz mit genau den Allowlist-Feldern des Modells (Diskriminante `model`). */
export const OdooRecordPayloadSchema = z.discriminatedUnion('model', [
  odooPartner,
  odooLead,
  odooOrder,
]);
export type OdooRecordPayload = z.infer<typeof OdooRecordPayloadSchema>;

/**
 * Laufende eines Odoo-Abrufs je Modell: `full = true` bei Vollabgleich (Grundlage des Mark-and-Sweep: Datensätze, die
 * seit `startedAt` nicht mehr gesehen wurden, gelten als in Odoo gelöscht).
 */
export const OdooRunEndPayloadSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal('run_end'),
  model: z.enum(ODOO_MODELS),
  full: z.boolean(),
  startedAt: instant,
});
export type OdooRunEndPayload = z.infer<typeof OdooRunEndPayloadSchema>;
